import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { IconButton, Modal, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/zoom';

import Slide from './Slide';
import useStyles from './useStyles';

const LABELS = {
  of: 'of',
  close: 'Close',
  prev: 'Previous',
  next: 'Next',
  error: "Image can't be displayed",
};

/**
 * Full-screen photo carousel over the whole window; `index` is controlled.
 * `images`: `{ id, src, previewSrc?, caption? }` — `previewSrc` (a smaller rendition) shows until `src`
 * has loaded; the active image's `caption` shows in brackets after the title (after the counter on mobile).
 */
const PhotoViewer = ({ open, images, index, onIndexChange, onClose, title, labels }) => {
  const classes = useStyles();
  const swiperRef = useRef(null);
  const text = { ...LABELS, ...labels };
  const caption = images[index]?.caption && ` (${images[index].caption})`;

  return (
    <Modal open={open && images.length > 0} onClose={onClose} hideBackdrop>
      <div className={classes.root}>
        <div className={classes.header}>
          {title && (
            <Typography variant="h5" noWrap className={classes.title}>
              {title}
              {caption}
            </Typography>
          )}
          <Typography variant="h6" noWrap className={classes.counter}>
            {`${index + 1} ${text.of} ${images.length}`}
            {/* the title hides on mobile, so the caption moves here; shown on desktop only without a title */}
            {caption && <span className={classnames(title && classes.mobileOnly)}>{caption}</span>}
          </Typography>
          <IconButton
            aria-label={text.close}
            className={classnames(classes.button, classes.close)}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.body}>
          <Swiper
            ref={swiperRef}
            className={classes.swiper}
            modules={[Keyboard, Zoom]}
            initialSlide={index}
            spaceBetween={16}
            roundLengths
            maxBackfaceHiddenSlides={0}
            keyboard
            zoom
            onSlideChange={({ activeIndex }) => onIndexChange(activeIndex)}
          >
            {images.map(({ id, src, previewSrc }, i) => (
              <SwiperSlide key={id}>
                <Slide
                  src={src}
                  previewSrc={previewSrc}
                  load={Math.abs(i - index) <= 1}
                  errorText={text.error}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {index > 0 && (
            <IconButton
              aria-label={text.prev}
              className={classnames(classes.button, classes.arrow, classes.prev)}
              onClick={() => swiperRef.current.swiper.slidePrev()}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {index < images.length - 1 && (
            <IconButton
              aria-label={text.next}
              className={classnames(classes.button, classes.arrow, classes.next)}
              onClick={() => swiperRef.current.swiper.slideNext()}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </div>
        <div className={classes.footer}></div>
      </div>
    </Modal>
  );
};

PhotoViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      previewSrc: PropTypes.string,
      caption: PropTypes.string,
    })
  ).isRequired,
  index: PropTypes.number.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  labels: PropTypes.objectOf(PropTypes.string),
};

PhotoViewer.defaultProps = {
  title: null,
  labels: LABELS,
};

export default PhotoViewer;
